import { useCallback, useMemo } from 'react';
import { effectivePermissions, isPermission, type Permission } from '@mustauto/types/permissions';
import { useAuth } from './useAuth';
import { useTeamMembers } from './useTeamMembers';

/**
 * O que quem está logado pode fazer NA OFICINA ATIVA (Fase 3 do plano de
 * permissões) — o efetivo que o `/me` devolve por oficina.
 *
 * É conveniência de tela, não autorização: a API barra do mesmo jeito. Sem a
 * lista (resposta antiga, ou ainda carregando), vale o padrão do papel da
 * oficina; o gestor sem oficina (acabou de se cadastrar) pode tudo, como hoje.
 */
export function useCan(): (permission: Permission) => boolean {
  const { user, workshops } = useAuth();
  const current = workshops.find((w) => w.isCurrent);
  const allowed = useMemo<ReadonlySet<Permission>>(() => {
    if (current?.permissions) return new Set(current.permissions.filter(isPermission));
    if (current) return effectivePermissions({ role: current.role, isOwner: current.isOwner });
    return effectivePermissions({ role: user?.role === 'manager' ? 'manager' : 'mechanic' });
  }, [current, user?.role]);
  return useCallback((permission: Permission) => allowed.has(permission), [allowed]);
}

/**
 * Quem pode fazer isto aqui, para a tela dizer "quem registra pagamento é a Ana
 * ou o Roberto" em vez de um botão morto (decisão 5 da prévia). Sem a lista da
 * equipe, nenhum nome — a frase cai no genérico.
 */
export function usePermissionHolders(permission: Permission): string[] {
  const { user } = useAuth();
  const { data } = useTeamMembers();
  return useMemo(
    () =>
      (data?.members ?? [])
        .filter((m) => m.userId !== user?.id && m.permissions.includes(permission))
        .map((m) => m.name.split(' ')[0] ?? m.name),
    [data, permission, user?.id],
  );
}

/** "a Ana ou o Roberto" sem gênero: "Ana ou Roberto"; nenhum nome, "o gestor". */
export function holdersText(names: string[]): string {
  if (names.length === 0) return 'o gestor';
  if (names.length === 1) return names[0]!;
  const shown = names.slice(0, 3);
  const rest = names.length - shown.length;
  const head = shown.slice(0, -1).join(', ');
  const last = shown[shown.length - 1]!;
  return rest > 0 ? `${shown.join(', ')} e mais ${rest}` : `${head} ou ${last}`;
}
